import { LOCATION_CHANGE } from 'react-router-redux'
import { RESET_ORDER } from './order'
import Immutable from 'immutable'

export const STATE_COMPLETE = 'complete';
export const STATE_INCOMPLETE = 'incomplete';
export const STATE_INVALID = 'invalid';
export const STATE_VALIDATING = 'validating';

export const ACTION_UPDATE_STEPS = 'takeback/order.steps/UPDATE_STEPS';
export const ACTION_COMPLETE_STEP = 'takeback/order.steps/COMPLETE_STEP';
export const ACTION_FAIL_STEP = 'takeback/order.steps/FAIL_STEP';
export const ACTION_GOTO_STEP = 'takeback/order.steps/GOTO_STEP';
export const ACTION_VALIDATE_STEP = 'takeback/order.steps/VALIDATE_STEP';
export const ACTION_RESET_STEP = 'takeback/order.steps/RESET_STEP';

export const ACTION_DISABLE_STEP = 'takeback/order.steps/DISABLE_STEP';
export const ACTION_ENABLE_STEP = 'takeback/order.steps/ENABLE_STEP';



const setIndexes = steps => {
    let index = 0;
    return steps.map((step, id) => step.merge({ id, index: !!step.get('enabled') ? index++ : null }));
};

const initialState = setIndexes(Immutable.fromJS({
    region: {
        route: '/order/region',
        state: STATE_INCOMPLETE,
        label: 'frontend.sidebar_order.step.region_selector',
        enabled: true
    },
    service: {
        route: '/order/service',
        state: STATE_INCOMPLETE,
        label: 'frontend.sidebar_order.step.service_selector',
        enabled: true
    },
    position: {
        route: '/order/position',
        state: STATE_INCOMPLETE,
        label: 'frontend.sidebar_order.step.position_sheet',
        enabled: true
    },
    shipping: {
        route: '/order/shipping',
        state: STATE_INCOMPLETE,
        label: 'frontend.sidebar_order.step.shipping_selector',
        enabled: true,
        errormessage: null
    },
    additions: {
        route: '/order/additions',
        state: STATE_INCOMPLETE,
        label: 'frontend.sidebar_order.step.additions',
        enabled: true
    },
    summary: {
        route: '/order/summary',
        state: STATE_INCOMPLETE,
        label: 'frontend.sidebar_order.step.order_summary',
        enabled: true
    },
    confirmation: {
        route: '/order/confirmation',
        state: STATE_INCOMPLETE,
        label: 'frontend.sidebar_order.step.order_confirmation',
        enabled: true
    },
})
    .toOrderedMap());


const routeMap = initialState.reduce((map, step) => {
    return map.set(step.get('route'), step.get('id'));
}, Immutable.Map());

/* Selectors */
export function getSteps(state) {
    return state.steps;
}

export function getEnabledSteps(state) {
    return filterEnabledSteps(state.steps);
}

export function filterEnabledSteps(steps) {
    return steps.filter(step => !!step.get('enabled'));
}

export function getStep(state, id) {
    return state.steps.get(id);
}

export function getNextStep(steps, currentStep) {
    const step = Immutable.Iterable.isIterable(currentStep) ? currentStep : steps.get(currentStep);
    return steps.get(steps.keySeq().get(step.get('index') + 1));
}

export function getPreviousStep(steps, currentStep) {
    const step = Immutable.Iterable.isIterable(currentStep) ? currentStep : steps.get(currentStep);
    return step.get('index') >= 1
        ? steps.get(steps.keySeq().get(step.get('index') - 1))
        : null;
}

export function getLastValidStep(steps) {
    const result = steps.findLast(step => step.get('state') === STATE_COMPLETE);
    return result || steps.get(steps.keySeq().first());
}

/* Reducers */
export default function reducer(state = initialState, action = {}) {
    switch (action.type) {
    // We listen for the react-router-redux's action LOCATION_CHANGE to sync the
    // active step with the url
    case LOCATION_CHANGE:
        if (!routeMap.has(action.payload.pathname)) {
            if (action.payload.location.pathname === '/order/region') {
                const completedStepsAfterRegion = state.filter(step => step.get('route') !== '/order/region' && step.get('state') === STATE_COMPLETE);
                if (completedStepsAfterRegion.size > 0) {
                    return initialState;
                }
            }
            return state;
        }

        return state.map(step => step
            .set('active', action.payload.pathname === step.get('route'))
            .remove('errors')
        );

    case RESET_ORDER: {
        return initialState;
    }

    case ACTION_UPDATE_STEPS:
        return action.payload;

    case ACTION_COMPLETE_STEP:
        return state
            .setIn([action.id, 'state'], STATE_COMPLETE)
            .removeIn([action.id, 'errors']);

    case ACTION_FAIL_STEP:
        return state.mergeIn([action.id], {
            state: STATE_INVALID,
            errormessage: action.message
        });

    case ACTION_VALIDATE_STEP:
        return state.setIn([action.id, 'state'], STATE_VALIDATING);

    case ACTION_RESET_STEP:
        return state.set(action.id, initialState.get(action.id));

    case ACTION_ENABLE_STEP:
        return setIndexes(state.setIn([action.id, 'enabled'], true));

    case ACTION_DISABLE_STEP:
        return setIndexes(state.setIn([action.id, 'enabled'], false));

    default: return state;
    }
}

/* Actions */
export function gotoStep(fromId, toId, validate = false) {
    return { type: ACTION_GOTO_STEP, from: fromId, to: toId, validate };
}

export function validateStep(id) {
    return { type: ACTION_VALIDATE_STEP, id };
}

export function completeStep(id) {
    return { type: ACTION_COMPLETE_STEP, id };
}

export function updateSteps(payload) {
    return {
        type: ACTION_UPDATE_STEPS,
        payload,
    };
}

export function failStep(id, error, message) {
    return { type: ACTION_FAIL_STEP, id, error: Immutable.fromJS(error), message };
}

export function resetStep(id) {
    return { type: ACTION_RESET_STEP, id };
}

export function enableStep(id) {
    return { type: ACTION_ENABLE_STEP, id };
}

export function disableStep(id) {
    return { type: ACTION_DISABLE_STEP, id };
}

export function nextStep(currentStep) {
    return (dispatch, getState) => {
        const steps = getEnabledSteps(getState());

        return dispatch(gotoStep(
            currentStep.get('id'),
            getNextStep(steps, currentStep).get('id'),
            true
        ));
    };
}

export function previousStep(currentStep) {
    return (dispatch, getState) => {
        const steps = getEnabledSteps(getState());

        return dispatch(gotoStep(
            currentStep.get('id'),
            getPreviousStep(steps, currentStep).get('id'),
            true
        ));
    };
}
